import type { EmojiProvider } from '@atlaskit/emoji';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import type { Space } from '@atlassian/rovo-spaces/controllers/space-store/types';

import type { AgentsListProps, ConversationListProps } from '../../common/types';
import type { ButtonMenuItemProps } from '../../common/ui/button-menu-item';

type OverrideBoundingBox = { maxWidth?: string; maxHeight?: string };

export const SideNavModes = {
	SIDEBAR: 'sidebar',
	MINI_MODAL: 'mini-modal',
	FULLSCREEN: 'fullscreen',
} as const;

export type SideNavMode = (typeof SideNavModes)[keyof typeof SideNavModes];

export interface RovoSideNavigationProps {
	/**
	 * A `testId` prop is provided for specified elements, which is a unique
	 * string that appears as a data attribute `data-testid` in the rendered code,
	 * serving as a hook for automated tests
	 */
	testId?: string;
	/* if the side navigation is open or closed */
	isOpen?: boolean;
	/* invoked when the X/close button is clicked or the backdrop is clicked */
	onClose: () => void;
	onNewChatButtonClick?: ButtonMenuItemProps['onClick'];
	onPulseClick?: ButtonMenuItemProps['onClick'];
	onTasksClick?: ButtonMenuItemProps['onClick'];
	onScheduledChatsClick?: ButtonMenuItemProps['onClick'];
	/* props related to the side navigation conversation list */
	conversationListProps: ConversationListProps;
	/* props related to the side navigation agents list */
	agentsListProps: AgentsListProps;
	/** will hide the agent create action when false as it requires permissions */
	canCreateAgents: boolean;
	mode: SideNavMode;
	overrideBoundingBox?: OverrideBoundingBox;
	cloudId: string;
	/** Organization ID used for Post Office targeting. */
	orgId?: string;
	/** Workspace ARI used for Post Office targeting. */
	workspaceAri?: string;
	/** Atlassian Account ID of the current user. Required for the recommended-space card Relay query. */
	userId?: string;
	/** For create Space source icons: same params as chat (`getAgentKnowledgeConfiguration` / connectors). */
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
	/**
	 * Whether the ADS navigation (left-nav) is enabled rather than the in-component navigation
	 */
	isADSNavigationEnabled?: boolean;
	/**
	 * Currently only used in VR test to disable animations. Mocking animation components (SlideIn, ExitingPersistence) is difficult
	 * because it disrupts the DOM structure and CSS layout contexts, which causes CSS Grid elements (like gridArea menu items) to disappear.
	 */
	shouldSkipAnimation?: boolean;
	isNewAllConversationScreenEnabled?: boolean;

	isSpinnerOverlayConversationIconEnabled?: boolean;

	/**
	 * When true, the Chats section will render as a scrollable list with a divider
	 * that appears when the user scrolls. Controlled by the `rovo_chat_enable_tasks` FG.
	 */
	isTasksEnabled?: boolean;
	/** Provider used by the Create Space emoji picker. When omitted the picker is hidden. */
	emojiProvider?: Promise<EmojiProvider>;
	/** Called when the user selects a space in the side nav. */
	onSelectSpace?: (space: Space) => void;
	onSelectRecommendedSpaces?: () => void;
	/** The ID of the currently selected space. */
	selectedSpaceId?: string;
	/** Called when the user clicks the "Create space" button next to the Chats section header. */
	onCreateSpaceClick?: () => void;
	/** Called after a space is successfully deleted. */
	onSpaceDelete?: (space: Space) => void;
	/** Called when the user confirms a rename. newName is the trimmed new name. */
	onSpaceRename?: (space: Space, newName: string) => void;
	/** Called after a space is successfully created via the Create Space modal. */
	onSpaceCreated?: (
		space: import('@atlassian/rovo-spaces/controllers/space-store/types').CreateSpaceResponse,
		emojiId?: string,
	) => void;
	/**
	 * Called when the user clicks the "View all spaces" link rendered under the
	 * Spaces section. When `undefined`, the link is hidden (mirroring the existing
	 * `onViewAllAgentsClick` pattern). Plumbed through to `SpaceMenuItems`.
	 */
	onViewAllSpacesClick?: () => void;
}
